import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { api } from "@/lib/auth";
import { AccountLayout } from "@/components/account/AccountLayout";
import { usePageMeta } from "@/hooks/usePageMeta";
import {
    Wallet, ShoppingCart, Receipt, CreditCard, Download, Loader2, ArrowRight,
    TrendingUp, ArrowDown, ArrowUp,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const cents = (c) => (c == null ? "—" : `$${(c / 100).toFixed(2)}`);

const QUICK_QTYS = [200, 500, 1000, 2500, 5000, 10000];

export default function AccountPlays() {
    usePageMeta({ title: "Plays · NOXN", description: "Your play balance, top-up and transaction history." });
    const [balance, setBalance] = useState(null);
    const [tiers, setTiers] = useState([]);
    const [minPurchase, setMinPurchase] = useState(200);
    const [txns, setTxns] = useState(null);
    const [qty, setQty] = useState(500);
    const [quote, setQuote] = useState(null);
    const [busy, setBusy] = useState(false);

    const reload = async () => {
        try {
            const [b, t, tx] = await Promise.all([
                api.get("/plays/balance"),
                api.get("/plays/tiers/active"),
                api.get("/plays/transactions"),
            ]);
            setBalance(b.data.balance || 0);
            setTiers(t.data.tiers || []);
            setMinPurchase(t.data.min_purchase || 200);
            setTxns(tx.data.transactions || []);
        } catch (e) {
            toast.error("Could not load your plays.");
        }
    };

    useEffect(() => { reload(); }, []);

    useEffect(() => {
        if (!qty || qty < minPurchase) { setQuote(null); return; }
        const ctrl = new AbortController();
        api.post("/plays/quote", { plays: Number(qty) }, { signal: ctrl.signal })
            .then(({ data }) => setQuote(data))
            .catch(() => setQuote(null));
        return () => ctrl.abort();
    }, [qty, minPurchase]);

    const checkout = async () => {
        setBusy(true);
        try {
            const { data } = await api.post("/plays/checkout", {
                plays: Number(qty),
                origin_url: window.location.origin,
            });
            if (data.checkout_url) window.location.href = data.checkout_url;
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not start checkout.");
        } finally { setBusy(false); }
    };

    const totalPurchased = useMemo(
        () => (txns || []).filter((t) => t.kind === "purchase").reduce((s, t) => s + t.plays, 0),
        [txns]
    );
    const totalUsed = useMemo(
        () => (txns || []).filter((t) => (t.plays || 0) < 0).reduce((s, t) => s - t.plays, 0),
        [txns]
    );

    return (
        <AccountLayout title="Plays">
            <div className="space-y-8" data-testid="account-plays-page">
                {/* Header + balance */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    <div className="lg:col-span-2 border border-[#FF5A00]/30 bg-gradient-to-br from-[#FF5A00]/10 via-transparent to-transparent p-8 lg:p-10">
                        <div className="font-mono-tech text-[10px] uppercase tracking-[0.35em] text-[#FF5A00] flex items-center gap-2">
                            <Wallet size={12} /> / Available balance
                        </div>
                        <div className="mt-4 font-display font-black text-white text-6xl lg:text-7xl tracking-tighter" data-testid="account-plays-balance">
                            {balance == null ? "…" : balance.toLocaleString()}
                        </div>
                        <div className="mt-2 text-sm text-slate-400">plays ready to use on campaigns</div>
                        <div className="mt-6 flex gap-3 flex-wrap">
                            <a href="#buy" data-testid="account-plays-buy-cta"
                                className="inline-flex items-center gap-2 bg-[#FF5A00] hover:bg-[#E65200] text-white px-5 py-3 text-xs font-semibold uppercase tracking-widest transition-colors">
                                <ShoppingCart size={13} /> Buy more plays
                            </a>
                            <Link to="/account/book" data-testid="account-plays-book-cta"
                                className="inline-flex items-center gap-2 border border-white/20 hover:border-white/40 text-white px-5 py-3 text-xs font-semibold uppercase tracking-widest transition-colors">
                                Book a campaign <ArrowRight size={13} />
                            </Link>
                        </div>
                    </div>
                    <div className="border border-white/10 p-6 space-y-4">
                        <StatRow k="Purchased" v={totalPurchased.toLocaleString()} icon={ArrowUp} />
                        <StatRow k="Used" v={totalUsed.toLocaleString()} icon={ArrowDown} tone="neg" />
                        <StatRow k="Lifetime value" v={cents((txns || []).filter((t) => t.kind === "purchase").reduce((s, t) => s + (t.amount_total_cents || 0), 0))} icon={TrendingUp} />
                    </div>
                </div>

                {/* Quick buy */}
                <section id="buy" className="border border-white/10 p-6 lg:p-8" data-testid="account-plays-quickbuy">
                    <div className="flex items-center gap-2 mb-6">
                        <ShoppingCart size={14} className="text-[#FF5A00]" />
                        <div className="font-mono-tech text-[10px] uppercase tracking-[0.35em] text-slate-400">/ Buy plays</div>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                        <div className="lg:col-span-2 space-y-6">
                            <div className="flex items-center gap-2 flex-wrap">
                                {QUICK_QTYS.filter((n) => n >= minPurchase).map((n) => (
                                    <button key={n} onClick={() => setQty(n)}
                                        data-testid={`plays-quick-${n}`}
                                        className={`px-4 py-2 text-xs font-mono-tech uppercase tracking-widest border transition-colors ${
                                            qty === n ? "border-[#FF5A00] bg-[#FF5A00]/10 text-white"
                                                    : "border-white/15 text-slate-400 hover:border-white/40"
                                        }`}
                                    >{n.toLocaleString()}</button>
                                ))}
                            </div>

                            <div>
                                <div className="text-[10px] font-mono-tech uppercase tracking-[0.25em] text-slate-400 mb-1.5">Custom quantity</div>
                                <div className="flex items-stretch gap-2">
                                    <button onClick={() => setQty(Math.max(minPurchase, Number(qty) - 100))}
                                        data-testid="plays-dec"
                                        className="w-12 border border-white/15 hover:border-[#FF5A00] text-lg">−</button>
                                    <input
                                        type="number" min={minPurchase} step={100}
                                        value={qty}
                                        onChange={(e) => setQty(Math.max(0, Math.floor(Number(e.target.value) || 0)))}
                                        data-testid="plays-input"
                                        className="w-full bg-transparent border border-white/20 focus:border-[#FF5A00] px-3 py-2.5 text-center text-2xl font-display font-black outline-none"
                                    />
                                    <button onClick={() => setQty(Number(qty) + 100)}
                                        data-testid="plays-inc"
                                        className="w-12 border border-white/15 hover:border-[#FF5A00] text-lg">+</button>
                                </div>
                                {qty < minPurchase && (
                                    <div className="mt-1 text-xs text-red-400">Minimum {minPurchase.toLocaleString()} plays.</div>
                                )}
                            </div>

                            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                                {tiers.map((t) => {
                                    const active = quote?.tier_id === t.id;
                                    return (
                                        <div key={t.id}
                                            className={`p-3 border ${active ? "border-[#FF5A00] bg-[#FF5A00]/10" : "border-white/10"}`}>
                                            <div className="text-xs font-display font-black text-white">{t.label}</div>
                                            <div className="text-[10px] font-mono-tech text-slate-500 mt-0.5">
                                                {t.min_plays.toLocaleString()}{t.max_plays ? `–${t.max_plays.toLocaleString()}` : "+"}
                                            </div>
                                            <div className="text-[#FF5A00] text-sm font-mono-tech mt-1">{cents(t.price_per_play_cents)}/play</div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        <div className="border border-[#FF5A00]/30 bg-[#FF5A00]/5 p-6 h-fit" data-testid="plays-quote-panel">
                            <div className="font-mono-tech text-[10px] uppercase tracking-[0.3em] text-[#FF5A00] mb-3">/ Live quote</div>
                            {quote ? (
                                <>
                                    <div className="text-sm text-slate-300">Tier</div>
                                    <div className="font-display font-black text-2xl text-white">{quote.label}</div>
                                    <div className="mt-3 text-sm text-slate-300">Subtotal</div>
                                    <div className="font-display font-black text-3xl text-white">{cents(quote.total_cents)}</div>
                                    <div className="mt-1 text-xs text-slate-500">
                                        {Number(qty).toLocaleString()} plays × {cents(quote.price_per_play_cents)}
                                    </div>
                                    <div className="mt-3 text-xs text-slate-400">+ GST 10% at checkout</div>
                                    <button
                                        onClick={checkout}
                                        disabled={busy || qty < minPurchase}
                                        data-testid="plays-checkout-btn"
                                        className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-[#FF5A00] hover:bg-[#E65200] disabled:opacity-40 text-white px-5 py-3 text-xs font-semibold uppercase tracking-widest transition-colors"
                                    >
                                        {busy ? <Loader2 size={13} className="animate-spin" /> : <CreditCard size={13} />}
                                        Continue to payment
                                    </button>
                                </>
                            ) : (
                                <div className="text-sm text-slate-400">Enter a valid quantity to see the price.</div>
                            )}
                        </div>
                    </div>
                </section>

                {/* Ledger */}
                <section className="border border-white/10" data-testid="account-plays-ledger">
                    <div className="p-6 border-b border-white/10 flex items-center gap-2">
                        <Receipt size={14} className="text-[#FF5A00]" />
                        <div className="font-mono-tech text-[10px] uppercase tracking-[0.35em] text-slate-400">/ Transaction history</div>
                    </div>
                    {txns == null ? (
                        <div className="p-8 text-center text-slate-500 text-sm">Loading…</div>
                    ) : txns.length === 0 ? (
                        <div className="p-8 text-center text-slate-500 text-sm">No transactions yet. Buy your first pack of plays above.</div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="text-left text-[10px] font-mono-tech uppercase tracking-[0.25em] text-slate-500 border-b border-white/10">
                                        <th className="p-3">When</th>
                                        <th className="p-3">Type</th>
                                        <th className="p-3">Plays</th>
                                        <th className="p-3">Tier / Ref</th>
                                        <th className="p-3">Amount</th>
                                        <th className="p-3"></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {txns.map((t) => (
                                        <tr key={t.id} className="border-b border-white/5" data-testid={`plays-txn-row-${t.id}`}>
                                            <td className="p-3 text-slate-300">{(t.created_at || "").slice(0, 19).replace("T", " ")}</td>
                                            <td className="p-3">
                                                <span className={`text-[10px] font-mono-tech uppercase tracking-[0.25em] border px-2 py-0.5 ${
                                                    t.kind === "purchase" ? "border-emerald-500/40 text-emerald-300" :
                                                    t.kind === "campaign_debit" ? "border-[#FF5A00]/40 text-[#FF5A00]" :
                                                    "border-white/15 text-slate-400"
                                                }`}>{t.kind.replace("_", " ")}</span>
                                            </td>
                                            <td className={`p-3 font-mono-tech ${t.plays > 0 ? "text-emerald-300" : "text-[#FF5A00]"}`}>
                                                {t.plays > 0 ? "+" : ""}{t.plays.toLocaleString()}
                                            </td>
                                            <td className="p-3 text-slate-300">
                                                {t.tier_label || (t.campaign_id ? `Campaign ${t.campaign_id.slice(0, 8)}…` : "—")}
                                            </td>
                                            <td className="p-3 text-white">{cents(t.amount_total_cents)}</td>
                                            <td className="p-3 text-right">
                                                {t.kind === "purchase" && t.session_id && (
                                                    <a href={`${API}/plays/purchases/${t.session_id}/invoice.pdf`}
                                                        target="_blank" rel="noreferrer"
                                                        data-testid={`plays-invoice-${t.id}`}
                                                        className="text-xs font-mono-tech uppercase tracking-widest text-slate-300 hover:text-[#FF5A00] inline-flex items-center gap-1">
                                                        <Download size={12} /> Invoice
                                                    </a>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </section>
            </div>
        </AccountLayout>
    );
}

const StatRow = ({ k, v, icon: Icon, tone }) => (
    <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm text-slate-400">
            {Icon && <Icon size={13} className={tone === "neg" ? "text-[#FF5A00]" : "text-emerald-400"} />}
            {k}
        </div>
        <div className="font-display font-black text-white">{v}</div>
    </div>
);
